import type { EN_DECK } from './en';

/**
 * العربية — the droom deck (VC-12). Modern Standard Arabic, plain and polite, second person: the register a customer
 * meets on a Gulf bank or government app. Proper nouns and established product terms stay in Latin script (KYC Hub,
 * KycHub, PAN, Aadhaar, DigiLocker, RBI V-CIP, IFSCA, OTP, PDF, JPG/PNG, Chrome, Safari, IST). `**bold**` markers and
 * `{slots}` are preserved exactly.
 *
 * Arabic is a PACK-GATED language: the runtime offers it only when the session read lists it (`packLocales`, be
 * V223 — the Gulf packs say `[en, ar]`). An India session never offers it.
 *
 * PROVENANCE (binding, see AR_DECK_PROVENANCE): MACHINE-DRAFTED translation of the English deck, 2026-10-31 — not a
 * native-speaker review, not tenant-ratified. Every string is a translation of the English source of record; nothing
 * is added. The consent and privacy notice the customer accepts is the SERVER's text (tenant-ratified, VS-08 / D-10),
 * not this deck. The consent-adjacent strings here need the standing VCIP-REG glance and the tenant's linguistic
 * ratification before a Gulf tenant goes live.
 */
export const AR_DECK_PROVENANCE = {
  locale: 'ar',
  status: 'machine-draft',
  ratification: 'awaiting tenant ratification',
  draftedOn: '2026-10-31',
  source: 'en',
} as const;

export const AR_DECK: Record<keyof typeof EN_DECK, string> = {
  // ── documentSteps deck ─────────────────────────────────────────────────────
  'droom.capture.grade.live': 'فُحصت جودتها على الجهاز',
  'droom.capture.grade.upload': 'مرفوعة — درجة أدلة أدنى',
  'droom.capture.grade.upload.chip': 'درجة أدنى',
  'droom.capture.grade.upload.explain': 'تحمل النسخة المرفوعة درجة أدلة أدنى — يؤكد موظف التحقق مستنداتك معك أثناء المكالمة.',
  'droom.selfie.title': 'التقط صورة ذاتية واضحة',
  'droom.selfie.sub': 'واجه الكاميرا في إضاءة متساوية وأبقِ وجهك كله داخل الشكل البيضاوي.',
  'droom.selfie.hint.lineup': 'أبقِ وجهك كله داخل الشكل البيضاوي.',
  'droom.selfie.hint.toodark': 'تبدو مظلمة قليلًا — انتقل إلى مكان أكثر إضاءة.',
  'droom.selfie.hint.toobright': 'تبدو ساطعة جدًا — ابتعد عن الضوء المباشر.',
  'droom.selfie.primer': 'سنستخدم الكاميرا الأمامية لصورتك الذاتية.',
  'droom.selfie.done.title': 'التُقطت الصورة الذاتية',
  'droom.selfie.done.sub': 'حُفظت صورتك الذاتية بأمان — لا شيء آخر عليك فعله هنا.',
  'droom.selfiedoc.title': 'أمسك مستندك بجانب وجهك',
  'droom.selfiedoc.sub': 'تُظهرك صورة واحدة مع مستندك معًا — أبقِ وجهك داخل الشكل البيضاوي والمستند داخل المربع.',
  'droom.selfiedoc.hint.lineup': 'أبقِ وجهك داخل الشكل البيضاوي ومستندك داخل المربع.',
  'droom.selfiedoc.hint.readable': 'أمسك المستند مسطّحًا لتبقى كل تفاصيله مقروءة.',
  'droom.selfiedoc.primer':
    'سنستخدم الكاميرا الأمامية — جهّز في يدك بطاقة PAN أو رخصة القيادة أو جواز السفر. يُرجى عدم عرض بطاقة Aadhaar أمام الكاميرا — فهي غير مطلوبة هنا أبدًا.',
  'droom.selfiedoc.done.title': 'التُقطت الصورة',
  'droom.selfiedoc.done.sub': 'حُفظت صورتك مع مستندك بأمان.',
  'droom.selfiedoc.unavailable': 'هذه الخطوة غير متاحة في هذا النظام بعد — سيلتقط موظف التحقق هذه الصورة معك أثناء المكالمة.',
  'droom.capture.assessing': 'جارٍ فحص الجودة على جهازك…',
  'droom.capture.uploading': 'جارٍ الرفع بأمان…',
  'droom.capture.preview.ok': 'تبدو واضحة — استخدم هذه الصورة، أو أعد الالتقاط.',
  'droom.capture.retake.note': 'لنحاول مرة أخرى — إعادة الالتقاط أمر طبيعي تمامًا.',
  'droom.capture.camera.starting': 'جارٍ تشغيل الكاميرا…',
  'droom.capture.camera.checking': 'جارٍ التحقق من الوصول إلى الكاميرا…',
  'droom.capture.camera.notready': 'ما زالت الكاميرا تبدأ — امنحها ثانية وحاول مرة أخرى.',
  'droom.capture.fallback.oncall': 'متابعة — التقط هذه الصورة مع موظف التحقق أثناء المكالمة',
  'droom.capture.uploadfailed': 'فشل الرفع — تحقق من اتصالك وحاول مرة أخرى.',
  'droom.upload.title': 'أضف مستنداتك',
  'droom.upload.sub': 'ارفع صورة واضحة لكل مستند مذكور أدناه.',
  'droom.upload.choose': 'اختر صورة',
  'droom.upload.replaceNot': 'النسخة الأولى التي نستلمها هي التي تُحتسب — لا يمكن استبدالها هنا.',
  'droom.upload.liveonly': 'يُصوَّر هذا المستند مباشرةً مع موظف التحقق أثناء المكالمة.',
  'droom.upload.required': 'مطلوب',
  'droom.upload.optional': 'اختياري',
  'droom.upload.progress': 'جارٍ الرفع بأمان…',
  'droom.upload.notavailable.title': 'رفع المستندات غير متاح هنا بعد',
  'droom.upload.notavailable.body': 'لا يدعم هذا النظام رفع المستندات بعد — ستُراجَع مستنداتك مع موظف التحقق أثناء المكالمة.',
  'droom.upload.loading': 'جارٍ التحقق من المستندات التي نحتاجها…',
  'droom.upload.loaderror': 'تعذّر تحميل قائمة مستنداتك. يُرجى المحاولة مرة أخرى.',
  'droom.capture.reject.blur': 'خرجت الصورة غير واضحة — ثبّت يدك وحاول مرة أخرى.',
  'droom.capture.reject.dark': 'الإضاءة خافتة قليلًا — انتقل إلى إضاءة أفضل وحاول مرة أخرى.',
  'droom.capture.reject.bright': 'يوجد انعكاس ساطع — أمِل المستند بعيدًا عن الضوء وأعد الالتقاط.',
  'droom.capture.reject.small': 'الصورة صغيرة جدًا لقراءتها بوضوح — جرّب جهازًا بكاميرا أوضح.',
  'droom.capture.reject.unreadable': 'تعذّرت معالجة الصورة — يُرجى المحاولة مرة أخرى.',
  'droom.upload.reject.file': 'هذا الملف لا يصلح — يُرجى رفع صورة واضحة (JPG أو PNG).',
  'droom.upload.reject.toolarge': 'هذا الملف كبير جدًا — يُرجى رفع صورة أصغر من 8 ميغابايت.',
  'droom.upload.reject.blur': 'تبدو هذه الصورة غير واضحة — الصورة الأوضح تُراجَع أسرع.',
  'droom.upload.reject.dark': 'تبدو هذه الصورة مظلمة — الصورة الملتقطة في إضاءة أفضل تُراجَع أسرع.',
  'droom.upload.reject.bright': 'تبدو هذه الصورة باهتة — الصورة الخالية من الوهج تُراجَع أسرع.',
  'droom.upload.reject.small': 'هذه الصورة صغيرة جدًا لقراءتها — الصورة الأكبر والأوضح تُراجَع أسرع.',
  'droom.upload.reject.unreadable': 'تعذّرت قراءة ذلك الملف — يُرجى اختيار صورة واضحة (JPG أو PNG).',

  // ── support deck ───────────────────────────────────────────────────────────
  'droom.support.trigger': 'هل تواجه صعوبة؟',
  'droom.support.title': 'احصل على مساعدة في هذه الخطوة',
  'droom.support.close': 'إغلاق',
  'droom.support.intro.named': 'إذا لم يكن شيء هنا يعمل، يمكن لـ {tenantName} مساعدتك.',
  'droom.support.intro.generic': 'إذا لم يكن شيء هنا يعمل، فالمساعدة متاحة.',
  'droom.support.none': 'لم تُدرج هذه الجهة جهة اتصال للدعم هنا. ردّ على الرسالة التي أرسلت إليك هذا الرابط، وسيساعدك شخص.',
  'droom.support.boundary': 'يمكن للدعم مساعدتك في استخدام هذه الصفحة. موظف التحقق وحده من يقرر نتيجة التحقق، أثناء المكالمة.',
  'droom.support.phone.label': 'الاتصال بالدعم',
  'droom.support.email.label': 'مراسلة الدعم بالبريد الإلكتروني',
  'droom.support.web.label': 'فتح صفحة المساعدة',
  'droom.support.hours': 'متاح {hours}',
  'droom.support.oncall': 'أنت الآن في مكالمة مع موظف التحقق — اسأله مباشرة وسيساعدك.',
  'droom.support.hint.welcome': 'تأكد أنك فتحت أحدث رابط أرسلناه إليك.',
  'droom.support.hint.schedule': 'اختر أي موعد يناسبك — يمكنك تغيير الموعد لاحقًا.',
  'droom.support.hint.consent': 'يلزم أن توافق قبل أن نتمكن من المتابعة. اقرأ النص كاملًا — لا يُشارَك أي شيء حتى توافق.',
  'droom.support.hint.confirm': 'راجع كل تفصيل بعناية. إذا كان هناك خطأ، فأخبر موظف التحقق أثناء المكالمة.',
  'droom.support.hint.pan': 'أدخل رقم PAN تمامًا كما هو مطبوع، دون مسافات.',
  'droom.support.hint.aadhaar': 'استخدم رمز OTP المرسل إلى رقم الجوال المرتبط بـ Aadhaar.',
  'droom.support.hint.capture': 'اختر إضاءة متساوية، وثبّت هاتفك، وأبقِ المستند كله داخل الإطار.',
  'droom.support.hint.selfie': 'واجه الكاميرا في إضاءة متساوية وأبقِ وجهك كله داخل الشكل البيضاوي.',
  'droom.support.hint.waiting': 'أبقِ هذه الصفحة مفتوحة — سينضم إليك موظف التحقق هنا. قد يؤدي مغادرتها إلى إنهاء جلستك.',
  'droom.support.hint.complete': 'لا شيء آخر عليك فعله هنا. يمكنك إغلاق هذه الصفحة.',
  'droom.support.hint.generic': 'حاول مرة أخرى باتصال أقوى، أو استخدم شبكة أخرى.',

  // ── shared actions & chrome ────────────────────────────────────────────────
  'droom.action.continue': 'متابعة',
  'droom.action.tryAgain': 'حاول مرة أخرى',
  'droom.action.retake': 'إعادة الالتقاط',
  'droom.action.checking': 'جارٍ التحقق…',
  'droom.action.skipStep': 'تخطَّ هذه الخطوة',
  'droom.action.pleaseWait': 'يُرجى الانتظار…',
  'droom.action.copyLink': 'نسخ رابط التحقق',
  'droom.action.linkCopied': 'تم نسخ الرابط ✓',
  'droom.action.allowed': 'لقد سمحت بالوصول',
  'droom.pill.secure': 'آمن',
  'droom.pill.encrypted': 'مشفّر',
  'droom.pill.verified': 'تم الإرسال',
  'droom.pill.queue': 'قائمة الانتظار المباشرة',
  'droom.pill.preparing': 'جارٍ التجهيز',
  'droom.poweredBy': 'بدعم من KYC Hub',
  'droom.error.tryAgain': 'يُرجى المحاولة مرة أخرى.',

  // ── shell ──────────────────────────────────────────────────────────────────
  'droom.shell.preparing': 'جارٍ تجهيز جلستك…',
  'droom.shell.entryError.title': 'تعذّر تحميل جلستك',
  'droom.shell.entryError.body':
    'يُرجى فتح رابط الدعوة الآمن الذي أرسلناه إليك (برسالة نصية أو بريد إلكتروني). إذا كنت في مكالمة بالفعل، فأعد فتح الرابط نفسه للعودة إليها.',
  'droom.shell.entryError.retry': 'إعادة المحاولة',
  'droom.shell.planError.title': 'تعذّر تحميل خطوات التحقق',
  'droom.shell.planError.body':
    'لم نتمكن من تأكيد ما يحتاجه هذا التحقق. يُرجى التحقق من اتصالك والمحاولة مرة أخرى — لن نبدأ حتى نتمكن من تحميل الخطوات الصحيحة.',
  'droom.shell.planError.retry': 'إعادة المحاولة',

  // ── welcome ────────────────────────────────────────────────────────────────
  'droom.welcome.title': 'لنتحقق\nمن هويتك',
  'droom.welcome.sub': 'فحص مرئي سريع وآمن لإكمال فتح حسابك. يستغرق نحو 5 دقائق.',
  'droom.welcome.trust.rbi.l': 'RBI V-CIP',
  'droom.welcome.trust.ifsca.l': 'IFSCA V-CIP',
  'droom.welcome.trust.rbi.s': 'خاضع للتنظيم',
  'droom.welcome.trust.tenant.l': 'فحص مرئي مباشر',
  'droom.welcome.trust.tenant.s': 'مع موظف',
  'droom.welcome.trust.enc.l': 'مشفّر',
  'droom.welcome.trust.enc.s': 'من طرف إلى طرف',
  'droom.welcome.trust.time.l': '~5 دقائق',
  'droom.welcome.trust.time.s': 'جلسة واحدة',
  'droom.welcome.cta': 'ابدأ',
  'droom.welcome.ref': 'المرجع',

  // ── landing ────────────────────────────────────────────────────────────────
  'droom.landing.subtitle': 'التحقق المرئي',
  'droom.landing.title': 'التحقق المرئي الخاص بك',
  'droom.landing.sub': 'مكالمة فيديو قصيرة وآمنة مع موظف تحقق لتأكيد هويتك. تستغرق نحو خمس دقائق.',
  'droom.landing.need.head': 'ما ستحتاج إليه',
  'droom.landing.need.id.t': 'وثيقة هوية سارية',
  'droom.landing.need.id.d': 'الأصلية — أبقِها في متناول يدك.',
  'droom.landing.need.cam.t': 'الكاميرا والميكروفون',
  'droom.landing.need.cam.d': 'سيطلب متصفحك الإذن — يُرجى الضغط على «السماح».',
  'droom.landing.need.loc.t': 'موقعك المباشر',
  'droom.landing.need.loc.d': 'يُلتقط بإذنك عند بدء المكالمة.',
  'droom.landing.need.place.t': 'مكان هادئ وجيد الإضاءة',
  'droom.landing.need.place.d': 'حتى يتمكن الموظف من رؤيتك وسماعك بوضوح.',
  'droom.landing.happen.head': 'ما الذي سيحدث',
  'droom.landing.happen.p1':
    'ستنضم إلى مكالمة فيديو **مسجَّلة** مع موظف تحقق، سيفحص وثيقة هويتك ويطرح عليك بعض الأسئلة. وبإذنك، يُلتقط موقعك المباشر أثناء المكالمة.',
  'droom.landing.happen.p2':
    'يصبح التسجيل وموقعك وإجاباتك جزءًا من سجل التحقق الخاص بك، ويُحتفَظ بها وفق ما تقتضيه الأنظمة. وقبل جمع أي شيء، سنعرض عليك كل إذن على الشاشة خطوة بخطوة.',
  'droom.landing.band.checking.t': 'جارٍ التحقق من جلستك…',
  'droom.landing.band.checking.b': 'لحظة واحدة — نؤكد هذا الرابط مع جهة التحقق الخاصة بك.',
  'droom.landing.band.preparing.t': 'بضع خطوات قبل مكالمتك',
  'droom.landing.band.preparing.b': 'ابدأ الآن — ستؤكد موافقتك وتصوّر هويتك وتلتقط صورة ذاتية، ثم نوصلك بموظف تحقق.',
  'droom.landing.band.joinable.t': 'جاهز متى كنت مستعدًا',
  'droom.landing.band.joinable.b': 'جلسة التحقق الخاصة بك جاهزة للانضمام.',
  'droom.landing.cta.join': 'انضم إلى مكالمة الفيديو',
  'droom.landing.cta.start': 'ابدأ التحقق',
  'droom.landing.session': 'الجلسة',
  'droom.landing.footer': 'أرسلت إليك جهة التحقق هذا الرابط الخاص — يُرجى عدم مشاركته.',
  'droom.landing.ended.t': 'انتهت جلسة التحقق هذه',
  'droom.landing.ended.b': 'لا يوجد ما تنضم إليه هنا. إذا كنت ما زلت بحاجة إلى إكمال التحقق، فاطلب من الجهة رابطًا جديدًا.',
  'droom.landing.notfound.t': 'تعذّر العثور على جلسة التحقق هذه',
  'droom.landing.notfound.b': 'تأكد أنك فتحت الرابط كاملًا من دعوتك — أو اطلب من جهة التحقق أن ترسل إليك رابطًا جديدًا.',

  // ── schedule ───────────────────────────────────────────────────────────────
  'droom.schedule.subtitle': 'الموعد',
  'droom.schedule.aria': 'حدّد موعد التحقق',
  'droom.schedule.checking': 'جارٍ التحقق من موعدك…',
  'droom.schedule.booked.title': 'موعدك',
  'droom.schedule.booked.sub': 'الأوقات معروضة بتوقيتك ({tz}). يُفتح الانضمام قبل موعدك بقليل — يمكنك متابعة خطوات التجهيز الآن.',
  'droom.schedule.booked.continue': 'متابعة التجهيز',
  'droom.schedule.booked.move': 'تغيير هذا الموعد…',
  'droom.schedule.held.title': 'تم حجز الوقت مؤقتًا',
  'droom.schedule.held.sub': 'يستمر هذا الحجز نحو 5 دقائق — أكّده للاحتفاظ بالوقت.',
  'droom.schedule.held.confirm': 'تأكيد هذا الوقت',
  'droom.schedule.held.confirming': 'جارٍ التأكيد…',
  'droom.schedule.held.different': 'اختر وقتًا آخر',
  'droom.schedule.pick.title': 'اختر وقتًا',
  'droom.schedule.pick.empty': 'لا أوقات متاحة في الأيام الثلاثة القادمة — يمكنك المتابعة الآن بدلًا من ذلك.',
  'droom.schedule.pick.now': 'المتابعة الآن',
  'droom.schedule.pick.skip': 'تخطٍّ — المتابعة الآن',
  'droom.schedule.moved.title': 'تقدّمت جلستك',
  'droom.schedule.moved.sub': 'أُغلق تحديد المواعيد لهذه الجلسة — تابع الخطوات التالية.',
  'droom.schedule.choice.title': 'متى تودّ إجراء التحقق؟',
  'droom.schedule.choice.sub': 'يستغرق التحقق المرئي نحو 10 دقائق. أجرِه الآن، أو احجز وقتًا يناسبك.',
  'droom.schedule.choice.pick': 'اختر وقتًا بدلًا من ذلك',
  'droom.schedule.note.taken': 'حُجز هذا الوقت للتو — اختر وقتًا آخر.',
  'droom.schedule.note.holdfail': 'حدث خطأ أثناء حجز ذلك الوقت — اختر مرة أخرى.',
  'droom.schedule.note.lapsed': 'انتهى الحجز (~5 دقائق) — اختر وقتًا مرة أخرى.',
  'droom.schedule.note.confirmfail': 'تعذّر التأكيد — اختر وقتًا مرة أخرى.',
  'droom.schedule.note.repick': 'اختر وقتك الجديد — يبقى موعدك الحالي قائمًا حتى يتأكد الجديد.',
  'droom.schedule.note.loadfail': 'تعذّر تحميل الأوقات — حاول مرة أخرى.',

  // ── consent ────────────────────────────────────────────────────────────────
  'droom.consent.subtitle': 'التحقق المرئي من الهوية',
  'droom.consent.title': 'خصوصيتك، وموافقتك',
  'droom.consent.sub': 'لن نستخدم بياناتك إلا للتحقق منك لأغراض التحقق المرئي من الهوية وفق {regime}. إليك بالضبط ما نعالجه ولماذا.',
  'droom.consent.process.head': 'ما نعالجه',
  'droom.consent.version.title': 'إصدار إشعار الموافقة',
  'droom.consent.version.unratified': 'لم يُقرّ فريق الامتثال هذه الصياغة بعد',
  'droom.consent.unratified': 'صياغة الإشعار بانتظار إقرار فريق الامتثال ({version}).',
  'droom.consent.readFull': 'اقرأ الإشعار كاملًا',
  'droom.consent.affirm':
    'أنا **أوافق** على أن تعالج KycHub بيانات هويتي للأغراض المذكورة أعلاه، لإكمال التحقق المرئي من الهوية لدى {tenantName}.',
  'droom.consent.agree': 'أوافق وأتابع',
  'droom.consent.recording': 'جارٍ تسجيل موافقتك…',
  'droom.consent.recorded': 'سُجّلت الموافقة ✓',
  'droom.consent.notNow': 'ليس الآن',
  'droom.consent.decline': 'لا أوافق',
  'droom.consent.declined.title': 'الموافقة مطلوبة للمتابعة',
  'droom.consent.declined.body':
    'التحقق المرئي من الهوية لدى {tenantName} تحقق خاضع للتنظيم — لا يمكننا المتابعة إلا بموافقتك على معالجة بيانات هويتك لأجله. لا يحدث شيء من دونها، ويمكنك سحب موافقتك أو طلب الحذف بعد انقضاء فترة الاحتفاظ الإلزامية.',
  'droom.consent.declined.review': 'مراجعة الموافقة مرة أخرى',
  'droom.consent.loading': 'جارٍ تحميل إشعار الموافقة…',
  'droom.consent.loadfail': 'تعذّر تحميل إشعار الموافقة',
  'droom.consent.resolved': 'سُجّلت الموافقة — جارٍ المتابعة…',
  'droom.consent.stampline': 'مختومة بالوقت · مرتبطة بعنوان IP · مسجّلة بالتجزئة · غير قابلة للتعديل',
  'droom.consent.purpose.video.t': 'مكالمة فيديو مسجَّلة',
  'droom.consent.purpose.video.d': 'تُسجَّل مكالمة التحقق هذه ويُحتفَظ بها دليلًا على التحقق من هويتك (KYC).',
  'droom.consent.purpose.bio.t': 'الوجه والحضور الحي',
  'droom.consent.purpose.bio.d': 'فحص مباشر لوجهك، تجري مطابقته مع وثيقة هويتك.',
  'droom.consent.purpose.loc.t': 'الموقع المباشر',
  'droom.consent.purpose.loc.d': 'يُلتقط مرة واحدة لتأكيد الولاية القضائية التي توجد فيها، كما تقتضي الجهة التنظيمية.',
  'droom.consent.purpose.ret.t': 'الاحتفاظ بالبيانات',
  'droom.consent.purpose.ret.d': 'يُحتفَظ ببيانات هويتك للمدة الإلزامية ولا تُباع أبدًا.',
  'droom.consent.purpose.dedup.t': 'فحص الاحتيال',
  'droom.consent.purpose.dedup.d':
    'لمنع الاحتيال في الهوية، قد تُقارَن بيانات وجهك بمحاولات تحقق أخرى لدى هذه المؤسسة. ولا تُشارَك أبدًا مع مؤسسات أخرى.',
  'droom.consentreq.title': 'نحتاج إلى موافقتك أولًا',
  'droom.consentreq.body.docs': 'سجل موافقتك على هذا التحقق مفقود، لذلك لا يمكننا قبول مستنداتك بعد. يُرجى مراجعة الموافقة وتأكيدها — وستعود مباشرة إلى هنا.',
  'droom.consentreq.body.photo': 'سجل موافقتك على هذا التحقق مفقود، لذلك لا يمكننا قبول صورتك بعد. يُرجى مراجعة الموافقة وتأكيدها — وستعود مباشرة إلى هنا.',
  'droom.consentreq.review': 'مراجعة الموافقة',

  // ── PAN ────────────────────────────────────────────────────────────────────
  'droom.pan.subtitle': 'التحقق من PAN',
  'droom.pan.title': 'بيانات PAN الخاصة بك',
  'droom.pan.sub': 'نتحقق من رقم PAN لدى الجهة المُصدِرة قبل مكالمة الفيديو — خطوة أقل أمام الكاميرا. ويؤكد موظف التحقق النتيجة أثناء المكالمة.',
  'droom.pan.checked.title': 'تم فحص PAN ✓',
  'droom.pan.checked.sub': 'فُحص لدى الجهة المُصدِرة. سيؤكد موظف التحقق ذلك أثناء المكالمة.',
  'droom.pan.checked.sub.masked': 'فُحص لدى الجهة المُصدِرة ({masked}). سيؤكد موظف التحقق ذلك أثناء المكالمة.',
  'droom.pan.label.pan': 'رقم PAN (كما هو مطبوع على بطاقتك)',
  'droom.pan.label.name': 'الاسم تمامًا كما هو على بطاقة PAN',
  'droom.pan.err.format': 'يتكوّن رقم PAN من 5 أحرف، ثم 4 أرقام، ثم حرف — مثل AAAAA9999A. يُرجى التحقق والمحاولة مرة أخرى.',
  'droom.pan.err.mismatch':
    'لم يطابق الاسم رقم PAN هذا لدى المصدر. قارن التهجئة ببطاقتك — أو تابع، وسيتحقق الموظف منه معك أثناء المكالمة.',
  'droom.pan.err.mismatch.score':
    'لم يطابق الاسم رقم PAN هذا لدى المصدر (التطابق {score}%). قارن التهجئة ببطاقتك — أو تابع، وسيتحقق الموظف منه معك أثناء المكالمة.',
  'droom.pan.err.invalid': 'لم تتعرّف الجهة المُصدِرة على رقم PAN هذا. يُرجى إعادة التحقق من الرقم — أو تابع، وسيتحقق الموظف منه معك أثناء المكالمة.',
  'droom.pan.err.provider': 'تعذّر الوصول إلى مصدر التحقق الآن. يمكنك المحاولة مرة أخرى — أو المتابعة، وسيتحقق الموظف من رقم PAN معك أثناء المكالمة.',
  'droom.pan.check': 'افحص رقم PAN',
  'droom.pan.checking': 'جارٍ الفحص…',
  'droom.pan.skip': 'تخطٍّ الآن — التحقق أثناء المكالمة بدلًا من ذلك',
  'droom.pan.fallback': 'متابعة — التحقق من رقم PAN مع الموظف أثناء المكالمة',
  'droom.pan.privacy':
    'يُرسَل رقم PAN مرة واحدة، مشفّرًا، للتحقق منه لدى الجهة المُصدِرة. ويُخزَّن دليلَ تحقق بموجب الإشعار الذي قبلته، ولا يُعرض على الشاشة مرة أخرى أبدًا.',
  'droom.pan.oncall.title': 'سنتحقق من رقم PAN أثناء المكالمة',
  'droom.pan.oncall.body': 'هذه الخطوة غير متاحة هنا الآن — سيتحقق موظف التحقق من رقم PAN معك أثناء مكالمة الفيديو. لم يُفقد شيء، ولا شيء آخر عليك فعله هنا.',

  // ── Aadhaar / DigiLocker ───────────────────────────────────────────────────
  'droom.aadhaar.subtitle': 'التحقق من Aadhaar',
  'droom.aadhaar.aria': 'التحقق من Aadhaar عبر DigiLocker',
  'droom.aadhaar.relaying': 'جارٍ إنهاء التحقق من Aadhaar…',
  'droom.aadhaar.checking': 'جارٍ التحقق من حالة التحقق الخاصة بك…',
  'droom.aadhaar.verified.title': 'تم التحقق من Aadhaar ✓',
  'droom.aadhaar.verified.sub': 'جرى التحقق من Aadhaar عبر DigiLocker. لا شيء آخر عليك فعله هنا.',
  'droom.aadhaar.unavailable.title': 'غير متاح الآن',
  'droom.aadhaar.unavailable.detail': 'خدمة Aadhaar عبر DigiLocker غير متاحة الآن. تابع — ستُراجَع مستنداتك معك أثناء المكالمة.',
  'droom.aadhaar.fallback.cta': 'متابعة — التحقق معي أثناء المكالمة',
  'droom.aadhaar.fallback.retry': 'جرّب DigiLocker مرة أخرى',
  'droom.aadhaar.fallback.error': 'تعذّر تأكيد ذلك الآن — يُرجى المحاولة بعد لحظة.',
  'droom.aadhaar.fallback.refused': 'تعذّر نقل هذه الخطوة إلى المكالمة — يبدو أن DigiLocker يعمل، لذا يُرجى تجربته مرة أخرى.',
  'droom.aadhaar.failed.expired': 'انتهت صلاحية محاولة DigiLocker تلك — يُرجى البدء من جديد.',
  'droom.aadhaar.failed.title': 'لم يكتمل ذلك',
  'droom.aadhaar.failed.incomplete': 'لم يكتمل التحقق.',
  'droom.aadhaar.failed.relay': 'تعذّر إنهاء التحقق — يمكنك إعادة المحاولة، أو المتابعة من دونه.',
  'droom.aadhaar.failed.state': 'لا يمكن تشغيل هذه الخطوة الآن. حاول مرة أخرى — أو سيتحقق الموظف من مستندك معك أثناء المكالمة.',
  'droom.aadhaar.failed.start': 'تعذّر بدء رحلة DigiLocker — أعد المحاولة، أو تابع من دونها.',
  'droom.aadhaar.offer.title': 'تحقق من Aadhaar عبر DigiLocker',
  'droom.aadhaar.offer.body':
    'ستنتقل إلى خدمة DigiLocker الحكومية لتسجيل الدخول باستخدام Aadhaar ورمز OTP. يجري ذلك على موقعهم — لا نرى رقم Aadhaar أبدًا، ولا تعود إلى هنا إلا نتيجة التحقق.',
  'droom.aadhaar.label.name': 'اسمك (كما هو في Aadhaar)',
  'droom.aadhaar.placeholder.name': 'الاسم',
  'droom.aadhaar.label.mobile': 'رقم الجوال',
  'droom.aadhaar.placeholder.mobile': 'جوال من 10 أرقام',
  'droom.aadhaar.err.mobile': 'أدخل رقم جوال هنديًا صالحًا (يجب أن يبدأ بـ 6 أو 7 أو 8 أو 9).',
  'droom.aadhaar.err.form': 'يُرجى التحقق من رقم جوالك والمحاولة مرة أخرى.',
  'droom.aadhaar.hint': 'اسمك ورقم جوالك يبدآن فقط تسجيل الدخول إلى DigiLocker — أما بياناتك المُتحقَّق منها فتعود من DigiLocker نفسه.',
  'droom.aadhaar.cta': 'التحقق عبر DigiLocker',
  'droom.aadhaar.starting': 'جارٍ البدء…',
  'droom.aadhaar.skip': 'تخطٍّ — التحقق من المستندات أثناء المكالمة بدلًا من ذلك',
  'droom.aadhaar.without': 'المتابعة من دونه',

  // ── capture ────────────────────────────────────────────────────────────────
  'droom.capture.subtitle': 'الخطوة 2 · الالتقاط',
  'droom.capture.doc.label': 'وثيقة الهوية',
  'droom.capture.doc.title': 'صوّر وثيقة هويتك',
  'droom.capture.doc.sub': 'ضع الوثيقة مسطّحة، واملأ بها الإطار، وتجنّب الوهج — يجب أن تكون كل التفاصيل مقروءة.',
  'droom.capture.selfie.label': 'صورة ذاتية',
  'droom.capture.selfie.title': 'والآن صورة ذاتية واضحة',
  'droom.capture.selfie.sub': 'واجه الكاميرا في إضاءة متساوية وأبقِ وجهك كله داخل الدليل.',
  'droom.capture.loading.title': 'جارٍ تجهيز الالتقاط',
  'droom.capture.loading.sub': 'نتحقق مما قد تكون رفعته من قبل.',
  'droom.capture.snag': 'واجهنا عقبة',
  'droom.capture.complete': 'اكتمل الالتقاط',
  'droom.capture.queue.entering': 'جارٍ إضافتك إلى قائمة انتظار موظفي التحقق…',
  'droom.capture.queue.failed': 'تعذّرت إضافتك إلى قائمة الانتظار — يُرجى المحاولة مرة أخرى.',
  'droom.capture.moment': 'لحظة واحدة…',
  'droom.capture.err.link': 'لم يعد رابط التحقق هذا صالحًا — يُرجى استخدام أحدث رابط أُرسل إليك.',
  'droom.capture.err.list': 'تعذّر فحص مستنداتك الملتقطة. يُرجى المحاولة مرة أخرى.',
  'droom.capture.err.queue': 'تعذّرت إضافتك إلى قائمة الانتظار. يُرجى التحقق من اتصالك والمحاولة مرة أخرى.',
  'droom.capture.flip': 'تبديل الكاميرا',
  'droom.capture.badge.rear': 'الكاميرا الخلفية',
  'droom.capture.badge.front': 'الكاميرا الأمامية',
  'droom.capture.badge.camera': 'الكاميرا',
  'droom.capture.badge.preview': 'معاينة',
  'droom.capture.retakeThis': 'يُرجى إعادة التقاط هذه الصورة.',
  'droom.capture.primer.rear': 'سنستخدم الكاميرا الخلفية لتصوير مستندك.',
  'droom.capture.hint.face': 'أبقِ وجهك كله داخل الدليل',
  'droom.capture.hint.doc': 'املأ الإطار بمستندك · تجنّب الوهج',
  'droom.capture.session.checking': 'جارٍ التحقق من جلستك…',
  'droom.capture.session.loadfail': 'تعذّر التحميل',
  'droom.capture.use': 'استخدم هذه الصورة',
  'droom.capture.turnon': 'تشغيل الكاميرا',
  'droom.capture.shoot': 'التقاط صورة',
  'droom.capture.assessing.short': 'جارٍ فحص الجودة…',
  'droom.capture.camera.startingBtn': 'جارٍ تشغيل الكاميرا…',
  'droom.capture.camera.checkingBtn': 'جارٍ فحص الكاميرا…',
  'droom.capture.queue.enteringBtn': 'جارٍ دخول قائمة الانتظار…',
  'droom.capture.tile.uploadedEarlier': 'رُفعت سابقًا',
  'droom.capture.tile.capturedEarlier': 'التُقطت سابقًا',
  'droom.capture.tile.uploading': 'جارٍ الرفع…',
  'droom.capture.tile.inprogress': 'قيد التنفيذ',
  'droom.capture.tile.next': 'التالي',
  'droom.capture.tile.hash': 'تجزئة السلامة من جهة الخادم (أول 12 خانة)',
  'droom.capture.footer': 'تُفحص الجودة على جهازك قبل رفع أي شيء',
  'droom.capture.previewAlt': 'معاينة صورتك الملتقطة',
  'droom.capture.doc.titleTyped': 'صوّر {doc}',
  'droom.selfiestep.subtitle': 'صورة ذاتية',
  'droom.selfiestep.tile': 'صورة ذاتية',
  'droom.selfiedocstep.subtitle': 'صورة مع مستندك',
  'droom.selfiedocstep.tile': 'أنت + المستند',
  'droom.selfiecore.loading.title': 'جارٍ تجهيز الكاميرا',
  'droom.selfiecore.loading.sub': 'نتحقق مما إذا كانت هذه الصورة قد التُقطت من قبل.',
  'droom.selfiecore.notavailable': 'غير متاح هنا بعد',
  'droom.uploadstep.subtitle': 'المستندات',
  'droom.uploadstep.none': 'لا يوجد ما ترفعه هنا — تُراجَع مستنداتك مع موظف التحقق أثناء المكالمة.',
  'droom.uploadstep.notyet': 'لم يُرفع بعد',
  'droom.uploadstep.oncall': 'يُعالَج أثناء مكالمتك',
  'droom.uploadstep.lockedCta': 'ارفع {doc} للمتابعة',
  'droom.uploadstep.uploadingAria': 'جارٍ رفع {doc}',
  'droom.uploadstep.footer': 'تُفحص الملفات على جهازك قبل رفع أي شيء',
  'droom.doctype.PAN_CARD': 'بطاقة PAN',
  'droom.doctype.AADHAAR': 'Aadhaar',
  'droom.doctype.DRIVING_LICENCE': 'رخصة القيادة',
  'droom.doctype.VOTER_ID': 'بطاقة الناخب',
  'droom.doctype.PASSPORT': 'جواز السفر',
  'droom.doctype.UTILITY_BILL': 'فاتورة خدمات',
  'droom.doctype.SELFIE': 'صورة ذاتية',
  'droom.doctype.SELFIE_WITH_DOC': 'صورة ذاتية مع مستندك',
  'droom.doctype.OTHER_DOCUMENT': 'مستند داعم',
  'droom.doctype.fallback': 'مستند',

  // ── steps / checks ─────────────────────────────────────────────────────────
  'droom.step.consent': 'الموافقة',
  'droom.step.capture': 'المستندات والصورة الذاتية',
  'droom.step.confirm': 'تأكيد بياناتك',
  'droom.step.waiting': 'غرفة الانتظار',
  'droom.step.call': 'مكالمة فيديو مباشرة',
  'droom.step.complete': 'اكتمل كل شيء',
  'droom.check.DIGILOCKER_AADHAAR': 'Aadhaar (DigiLocker)',
  'droom.check.PAN_SOURCE': 'التحقق من PAN',
  'droom.check.DOC_CAPTURE': 'التقاط المستند',
  'droom.check.CONSENT': 'الموافقة',
  'droom.check.FACE_MATCH': 'مطابقة الوجه',
  'droom.check.LIVENESS': 'الحضور الحي',
  'droom.check.GEO_IP': 'فحص الموقع',
  'droom.check.AGENT_QUESTIONS': 'أسئلة الموظف',
  'droom.check.AML_SCREENING': 'الفحص',
  'droom.check.SELFIE': 'صورة ذاتية',
  'droom.check.SELFIE_WITH_DOC': 'صورة مع مستندك',
  'droom.missing.aria': '{label} — يُعالَج أثناء المكالمة',
  'droom.missing.body': 'تُستكمل هذه الخطوة مع موظف التحقق أثناء المكالمة المباشرة — لا شيء عليك فعله هنا الآن. تابع إلى الخطوة التالية.',

  // ── waiting room / arrival ─────────────────────────────────────────────────
  'droom.wait.subtitle': 'غرفة الانتظار',
  'droom.wait.officerJoined': 'انضم موظف التحقق — جارٍ توصيلك الآن',
  'droom.wait.finding': 'جارٍ تحديد مكانك في الدور…',
  'droom.wait.next': 'أنت التالي — سنوصلك فور أن يتفرغ موظف',
  'droom.wait.position': 'ترتيبك {position} في الدور لموظف التحقق التالي',
  'droom.wait.eta': 'وقت الانتظار المتوقع ~{min} دقيقة',
  'droom.wait.progressAria': 'تقدّم قائمة الانتظار',
  'droom.wait.keepOpen': 'أبقِ هذه الشاشة مفتوحة',
  'droom.wait.autoconnect': 'سنوصلك عندما ينضم موظف',
  'droom.wait.spotSaved': 'مكانك محفوظ',
  'droom.wait.reschedule': 'تغيير الموعد إلى وقت لاحق',
  'droom.wait.rescheduling': 'جارٍ فتح المواعيد…',
  'droom.wait.refusal.title': 'لا يمكننا إضافتك إلى قائمة الانتظار بعد',
  'droom.wait.refusal.body': 'ما زال شيء من خطوة سابقة بحاجة إلى إكمال:',
  'droom.wait.refusal.goback': 'ارجع وأكمله',
  'droom.arrival.subtitle': 'موعدك',
  'droom.arrival.title': 'تم تحديد موعد التحقق',
  'droom.arrival.ist': '{time} بتوقيت الهند (IST)',
  'droom.arrival.body': 'أنت مبكر — لا يوجد أي خطأ. سنُدخلك إلى قائمة الانتظار تلقائيًا عند حلول موعدك.',
  'droom.arrival.countdown': 'يُفتح خلال {time}',
  'droom.arrival.countdownAria': 'الوقت المتبقي حتى فتح موعدك',
  'droom.arrival.autoconnect': 'نُدخلك تلقائيًا',
  'droom.arrival.stepsSaved': 'خطواتك المكتملة محفوظة',
  'droom.arrival.missed.title': 'انقضت الفترة المحددة لموعدك',
  'droom.arrival.missed.was': 'كان موعدك {time}.',
  'droom.arrival.missed.body': 'تعذّر توصيلك خلال تلك الفترة، لذا يلزم وقت جديد. يبقى كل ما أكملته محفوظًا.',
  'droom.arrival.missed.cta': 'اختر وقتًا جديدًا',

  // ── live call: location + permissions ──────────────────────────────────────
  'droom.live.subtitle': 'التحقق المباشر',
  'droom.geo.explain.title': 'شارك موقعك المباشر',
  'droom.geo.explain.body':
    'يتضمن هذا التحقق موقعك المباشر — إذ تؤكد إجراءات مؤسستك مكان وجودك أثناء مكالمة الفيديو، بما يتوافق مع إرشادات RBI V-CIP. يُلتقط مرة واحدة، بإذنك، ويُخزَّن جزءًا من سجل التحقق الخاص بك.',
  'droom.geo.explain.body.tenant':
    'يتضمن هذا التحقق موقعك المباشر — إذ تؤكد إجراءات مؤسستك البلد الذي تتصل منه أثناء مكالمة الفيديو. يُلتقط مرة واحدة، بإذنك، ويُخزَّن جزءًا من سجل التحقق الخاص بك.',
  'droom.geo.explain.body.ifsca':
    'يتضمن هذا التحقق موقعك المباشر — إذ تؤكد إجراءات مؤسستك الولاية القضائية التي تتصل منها أثناء مكالمة الفيديو، بما يتوافق مع إرشادات IFSCA الخاصة بالتحقق من هوية العملاء (KYC). يُلتقط مرة واحدة، بإذنك، ويُخزَّن جزءًا من سجل التحقق الخاص بك.',
  'droom.geo.explain.cta': 'شارك موقعي',
  'droom.geo.captured.title': 'سُجّل الموقع',
  'droom.geo.getting.title': 'جارٍ تحديد موقعك…',
  'droom.geo.captured.body': 'شكرًا لك — جارٍ توصيلك بموظف التحقق.',
  'droom.geo.getting.body': 'إذا طلب متصفحك ذلك، فيُرجى الضغط على «السماح».',
  'droom.geo.denied.title': 'رُفض إذن الموقع',
  'droom.geo.denied.body':
    'يمكن أن تستمر المكالمة — سيرى موظف التحقق أن دليل الموقع غير موجود وقد يسألك عنه. لمشاركته، اسمح بالموقع لهذا الموقع في إعدادات متصفحك، ثم حاول مرة أخرى.',
  'droom.geo.denied.howto':
    'طريقة السماح: اضغط على القفل (أو ⓘ) بجانب شريط العنوان ← الأذونات ← الموقع ← السماح، ثم اضغط «حاول مرة أخرى». وعلى iPhone تحقق أيضًا من الإعدادات ← الخصوصية والأمن ← خدمات الموقع ← متصفحك ← أثناء استخدام التطبيق. وإذا كان الموقع مغلقًا على الجهاز كله، فشغّله من الإعدادات السريعة أولًا.',
  'droom.geo.without': 'المتابعة دون الموقع',
  'droom.geo.postfailed.title': 'تعذّر تسجيل موقعك',
  'droom.geo.degraded.title': 'تعذّر تحديد موقعك',
  'droom.geo.degraded.fallback': 'حدث خطأ أثناء تحديد موقعك.',
  'droom.geo.degraded.body': 'يمكنك المحاولة مرة أخرى، أو المتابعة — سيرى الموظف أن دليل الموقع غير موجود.',
  'droom.perm.checking': 'جارٍ التحقق من الكاميرا والميكروفون…',
  'droom.perm.primer.title': 'شغّل الكاميرا والميكروفون',
  'droom.perm.primer.body': 'تلزم الكاميرا والميكروفون لمكالمة التحقق المباشرة مع الموظف. سيطلب متصفحك ذلك الآن — يُرجى الضغط على **السماح**.',
  'droom.perm.requesting.title': 'في انتظار إذنك…',
  'droom.perm.requesting.body': 'إذا كان متصفحك يطلب ذلك، فيُرجى الضغط على **السماح**.',
  'droom.perm.denied.title': 'الكاميرا والميكروفون محظوران',
  'droom.perm.denied.body': 'يحظر متصفحك الوصول، وأنت وحدك من يمكنه إعادة تشغيله — لا يمكن لأي موقع أن يطلب ذلك مرة أخرى نيابةً عنك.',
  'droom.perm.osdenied.title': 'اسمح بالوصول إلى الكاميرا في إعدادات النظام',
  'droom.perm.osdenied.body': 'تعذّر على متصفحك عرض طلب الإذن — الوصول مغلق على مستوى النظام. ستحتاج إلى تغيير ذلك بنفسك.',
  'droom.perm.steps.aria': 'طريقة السماح بالوصول',
  'droom.perm.autoresume': 'سنعيد الاتصال تلقائيًا لحظة سماحك بالوصول.',
  'droom.perm.nodevice.title': 'لم يُعثر على كاميرا أو ميكروفون',
  'droom.perm.nodevice.fallback': 'لا تتوفر في هذا الجهاز كاميرا أو ميكروفون للمكالمة.',
  'droom.perm.nodevice.advice': 'يُرجى استخدام جهاز بكاميرا وميكروفون يعملان.',
  'droom.perm.webview.title': 'افتح هذا الرابط في متصفحك',
  'droom.perm.webview.body': 'فتحت هذا من داخل تطبيق آخر لا يستطيع استخدام الكاميرا بشكل موثوق. يُرجى فتح هذا الرابط في **Chrome** أو **Safari** لمتابعة التحقق.',
  'droom.perm.webview.tip': 'نصيحة: اضغط على قائمة ••• واختر «الفتح في المتصفح».',
  'droom.perm.unsupported.title': 'لا يستطيع هذا المتصفح تشغيل المكالمة',
  'droom.perm.unsupported.body':
    'لا يدعم متصفحك الوصول المباشر إلى الكاميرا. يُرجى فتح هذا الرابط في إصدار حديث من Chrome أو Safari — على iPhone يعني ذلك iOS 14.3 أو أحدث؛ وعلى Android، ‏Chrome 90 أو أحدث.',
  'droom.perm.osnotice.ios':
    'برنامج iPhone لديك أقدم مما تدعمه هذه المكالمة ({min} أو أحدث). ما زال بإمكانك المحاولة، لكن إذا تعطّلت الكاميرا، فيُرجى تحديث iPhone من الإعدادات ← عام ← تحديث البرامج ثم إعادة فتح هذا الرابط.',
  'droom.perm.osnotice.android':
    'متصفحك أقدم مما تدعمه هذه المكالمة ({min} أو أحدث). ما زال بإمكانك المحاولة، لكن إذا تعطّلت الكاميرا، فيُرجى تحديث Chrome من Play Store ثم إعادة فتح هذا الرابط.',
  'droom.perm.osnotice.samsung':
    'متصفحك أقدم مما تدعمه هذه المكالمة ({min} أو أحدث). ما زال بإمكانك المحاولة، لكن إذا تعطّلت الكاميرا، فيُرجى تحديث Samsung Internet — أو فتح هذا الرابط في Chrome — والمحاولة مرة أخرى.',
  'droom.perm.rec.os.1': 'تعذّر على متصفحك عرض طلب الإذن — الحظر على مستوى النظام.',
  'droom.perm.rec.os.2': 'افتح إعدادات جهازك ← الخصوصية ← الكاميرا (والميكروفون) واسمح بالوصول لمتصفحك.',
  'droom.perm.rec.os.3': 'عُد إلى هنا واضغط «لقد سمحت بالوصول».',
  'droom.perm.rec.chrome.1': 'اضغط على أيقونة الكاميرا / القفل عند طرف شريط العنوان.',
  'droom.perm.rec.chrome.2': 'اضبط الكاميرا والميكروفون على السماح.',
  'droom.perm.rec.chrome.3': 'اضغط «لقد سمحت بالوصول» (لا حاجة لإعادة التحميل).',
  'droom.perm.rec.firefox.1': 'اضغط على أيقونة القفل في شريط العنوان، ثم إعدادات الاتصال / الأذونات.',
  'droom.perm.rec.firefox.2': 'امسح إدخالات الكاميرا والميكروفون المحظورة واضبطها على السماح.',
  'droom.perm.rec.firefox.3': 'اضغط «لقد سمحت بالوصول».',
  'droom.perm.rec.safari.1': 'افتح Safari ← الإعدادات لهذا الموقع (أو قائمة aA ← إعدادات موقع الويب).',
  'droom.perm.rec.safari.2': 'اضبط الكاميرا والميكروفون على السماح، ثم أعد التحميل إذا طُلب منك ذلك.',
  'droom.perm.rec.safari.3': 'اضغط «لقد سمحت بالوصول».',
  'droom.perm.rec.generic.1': 'افتح إعدادات الموقع في متصفحك لهذه الصفحة.',
  'droom.perm.rec.generic.2': 'اضبط الكاميرا والميكروفون على السماح.',
  'droom.perm.rec.generic.3': 'اضغط «لقد سمحت بالوصول».',
  'droom.cam.denied.title': 'الوصول إلى الكاميرا محظور',
  'droom.cam.denied.body': 'يحظر متصفحك الكاميرا، وأنت وحدك من يمكنه إعادة تشغيلها — لا يمكن لأي موقع أن يطلب ذلك مرة أخرى نيابةً عنك.',
  'droom.cam.osdenied.title': 'اسمح بالوصول إلى الكاميرا في إعدادات النظام',
  'droom.cam.osdenied.body': 'تعذّر على متصفحك عرض طلب الإذن — الوصول مغلق على مستوى النظام. ستحتاج إلى تغيير ذلك بنفسك.',
  'droom.cam.nodevice.title': 'لم يُعثر على كاميرا',
  'droom.cam.nodevice.fallback': 'لا يحتوي هذا الجهاز على كاميرا صالحة للاستخدام. يُرجى المتابعة على جهاز بكاميرا تعمل.',
  'droom.cam.webview.title': 'افتح هذا الرابط في متصفحك',
  'droom.cam.webview.body': 'فتحت هذا من داخل تطبيق آخر لا يستطيع استخدام الكاميرا بشكل موثوق. يُرجى فتح هذا الرابط في Chrome أو Safari للمتابعة.',
  'droom.cam.unsupported.title': 'لا يستطيع هذا المتصفح التقاط الصور',
  'droom.cam.unsupported.body': 'الوصول المباشر إلى الكاميرا غير متاح هنا. يُرجى فتح هذا الرابط في إصدار حديث من Chrome أو Safari.',
  'droom.cam.nodevice.hookDetail': 'لم يُعثر على كاميرا صالحة للاستخدام على هذا الجهاز.',
  'droom.perm.nodevice.hookDetail': 'لم يُعثر على كاميرا أو ميكروفون على هذا الجهاز.',

  // ── live call ──────────────────────────────────────────────────────────────
  'droom.call.connectingNamed': 'جارٍ توصيلك بـ {name}…',
  'droom.call.connecting': 'جارٍ توصيلك بموظف التحقق…',
  'droom.call.verifierAway.title': 'موظف التحقق يعيد الاتصال',
  'droom.call.verifierAway.body': 'يُرجى البقاء في المكالمة — قد يستغرق ذلك حتى دقيقة. نعتذر عن هذه الصعوبة.',
  'droom.call.connectingSlow.body': 'يستغرق هذا وقتًا أطول من المعتاد — يُرجى البقاء على هذه الشاشة.',
  'droom.call.verifierLost.title': 'تُنهى هذه المكالمة',
  'droom.call.verifierLost.body': 'سيتواصل معك موظف التحقق مرة أخرى لإعادة بدء التحقق. نعتذر عن الإزعاج.',
  'droom.call.verifierLost.support': 'يمكنك إغلاق هذه الصفحة الآن.',
  'droom.call.cameraLost.body': 'توقفت الكاميرا مؤقتًا أثناء غيابك — جارٍ إعادة توصيلها الآن…',
  'droom.call.resumeMedia.cta': 'اضغط لاستئناف الكاميرا والصوت',
  'droom.call.verifierNamed': '{name} · موظف التحقق',
  'droom.call.verifier': 'موظف التحقق',
  'droom.call.rec': 'تسجيل · مشفّر',
  'droom.call.swapTiles': 'تبديل العرض',
  'droom.call.swapTiles.showVerifier': 'عرض موظف التحقق بحجم كبير',
  'droom.call.swapTiles.showMe': 'عرضي بحجم كبير',
  'droom.call.guidance.label': 'إرشادات على الشاشة',
  'droom.call.guidance.text': 'أبقِ وجهك في المنتصف وفي إضاءة جيدة. سيرشدك موظف التحقق خلال الخطوات بعد الاتصال.',
  'droom.call.mute': 'كتم الميكروفون',
  'droom.call.unmute': 'إلغاء كتم الميكروفون',
  'droom.call.end': 'إنهاء المكالمة',
  'droom.call.camOff': 'إيقاف الكاميرا',
  'droom.call.camOn': 'تشغيل الكاميرا',
  'droom.call.leave.title': 'مغادرة المكالمة؟',
  'droom.call.leave.body': 'لم يكتمل التحقق بعد. إذا غادرت الآن، يمكنك العودة والمتابعة مع الموظف نفسه.',
  'droom.call.leave.stay': 'البقاء في المكالمة',
  'droom.call.leave.leave': 'مغادرة المكالمة',
  'droom.call.left.title': 'غادرت المكالمة',
  'droom.call.left.body': 'لم يكتمل التحقق. عُد إلى المكالمة لتتابع من حيث توقفت — يُحتفَظ بموظفك وبتقدّمك.',
  'droom.call.left.rejoin': 'العودة إلى المكالمة',
  'droom.call.superseded.title': 'تابعت على جهاز آخر',
  'droom.call.superseded.body':
    'التحقق مفتوح الآن على الجهاز الآخر — تابع هناك وسينتقل تقدّمك معك. لم يُفقد أي شيء، ولا شيء عليك فعله على هذه الشاشة.',
  'droom.call.expired.title': 'انتهت هذه الجلسة',
  'droom.call.expired.body':
    'ربما انتهت صلاحية الرابط. إذا لم يكتمل التحقق، فيُرجى استخدام رابطك الأصلي للبدء من جديد، أو التواصل مع {tenantName} للحصول على رابط جديد.',
  'droom.rc.lost': 'انقطع الاتصال',
  'droom.rc.reconnecting': 'جارٍ إعادة الاتصال…',
  'droom.rc.sub.suspended': 'تقدّمك محفوظ. عندما تكون مستعدًا، عُد إلى المكالمة — ستتابع تمامًا من حيث توقفت، مع موظف التحقق نفسه.',
  'droom.rc.sub.blip': 'تقدّمك محفوظ. يُرجى البقاء على هذه الشاشة — سنتابع تمامًا من حيث توقفت.',
  'droom.rc.kept': 'حُفظت الخطوة {step} من {total}',
  'droom.rc.kept.same': 'حُفظت الخطوة {step} من {total} · موظف التحقق نفسه',
  'droom.rc.rejoin': 'العودة الآن',

  // ── verifier-requested capture ─────────────────────────────────────────────
  'droom.gcap.badge': 'طلب من موظف التحقق',
  'droom.gcap.facing.rear': 'طلب موظف التحقق استخدام الكاميرا الخلفية — فهي تصوّر المستندات بوضوح أكبر. سيتبدّل فيديو مكالمتك أثناء التقاط هذه الصورة، ثم يعود بعدها.',
  'droom.gcap.facing.front': 'طلب موظف التحقق استخدام الكاميرا الأمامية لهذه الصورة. سيتبدّل فيديو مكالمتك أثناء التقاطها، ثم يعود بعدها.',
  'droom.gcap.facing.applied.rear': 'نستخدم الكاميرا الخلفية لهذه الصورة، كما طلب موظف التحقق. يعود فيديو مكالمتك عند انتهاء الصورة.',
  'droom.gcap.facing.applied.front': 'نستخدم الكاميرا الأمامية لهذه الصورة، كما طلب موظف التحقق.',
  'droom.gcap.facing.oneCamera': 'في هذا الجهاز كاميرا واحدة، لذا نستخدمها — ما زال بإمكانك التقاط الصورة.',
  'droom.gcap.facing.unavailable': 'تعذّر تبديل الكاميرا على هذا الجهاز، لذا سنستخدم الكاميرا الحالية. ما زال بإمكانك التقاط الصورة.',
  'droom.gcap.facing.unconfirmed': 'تغيّرت الكاميرا، لكن هذا الجهاز لا يُبلغ أيها قيد الاستخدام الآن. يُرجى مراجعة المعاينة أدناه قبل التقاط الصورة.',
  'droom.gcap.purpose.title': 'قبل أن نلتقط هذه الصورة',
  'droom.gcap.purpose.what': 'سنلتقط صورة لوجهك أو لمستندك، من هذه المكالمة.',
  'droom.gcap.purpose.comparedWith': 'تُقارَن بصورتك في Aadhaar وبوثيقة الهوية التي تقدّمها، للتأكد من أنك الشخص الذي تقول إنك هو.',
  'droom.gcap.purpose.processor': 'نتولى معالجتها نحن وشريكنا في التحقق، في الهند.',
  'droom.gcap.purpose.retention': 'يُحتفَظ بها جزءًا من سجل التحقق الخاص بك، طوال المدة التي يُلزمنا القانون بالاحتفاظ بها.',
  'droom.gcap.purpose.noTraining': 'لا تُستخدم أبدًا لتدريب أي نموذج ذكاء اصطناعي أو تحسينه.',
  'droom.gcap.purpose.withdraw': 'يمكنك التوقف في أي لحظة والتحدث إلى موظف التحقق أثناء المكالمة.',
  'droom.gcap.purpose.agree': 'فهمت — متابعة',
  'droom.gcap.attempts.exhausted': 'تعذّر الحصول على صورة واضحة بما يكفي. يُرجى التحدث إلى موظف التحقق أثناء المكالمة — سيساعدك.',
  'droom.gcap.asked': 'طلب موظف التحقق هذه الصورة أثناء المكالمة.',
  'droom.gcap.doc.title': 'اعرض مستندك أمام الكاميرا',
  'droom.gcap.doc.instruction': 'أمسك المستند مسطّحًا داخل المربع، وقريبًا بما يكفي لتكون كل تفاصيله مقروءة.',
  'droom.gcap.selfiedoc.title': 'أمسك مستندك بجانب وجهك',
  'droom.gcap.selfiedoc.instruction': 'تُظهرك صورة واحدة مع مستندك معًا — الوجه داخل الشكل البيضاوي، والمستند داخل المربع.',
  'droom.gcap.selfie.title': 'التقط صورة ذاتية مباشرة',
  'droom.gcap.selfie.instruction': 'انظر مباشرة إلى الكاميرا ووجهك كله داخل الشكل البيضاوي، في إضاءة جيدة.',
  'droom.gcap.cat.unclear': 'النسخة المحفوظة يصعب قراءتها، لذا تلزم صورة أوضح.',
  'droom.gcap.cat.mismatch': 'بعض التفاصيل تحتاج إلى مراجعة جديدة مقابل مستندك.',
  'droom.gcap.cat.tamper': 'يحتاج موظف التحقق إلى رؤية المستند الأصلي مباشرة أمام الكاميرا.',
  'droom.gcap.cat.identity': 'يحتاج موظف التحقق إلى التأكد مجددًا من أنك أنت من يحمل مستندك.',
  'droom.gcap.cat.regulatory': 'هذه الصورة مطلوبة في المكالمة المسجَّلة لإتمام التحقق.',
  'droom.gcap.cat.flow': 'هذه الصورة جزء من رحلة التحقق الخاصة بك — سيرشدك موظف التحقق.',
  'droom.gcap.cat.default': 'يحتاج موظف التحقق إلى هذه الصورة لمتابعة التحقق.',
  'droom.gcap.done.title': 'أُرسلت الصورة إلى موظف التحقق',
  'droom.gcap.done.sub': 'جارٍ العودة إلى مكالمتك…',
  'droom.gcap.back': 'العودة إلى المكالمة',
  'droom.gcap.send': 'إرسال هذه الصورة',
  'droom.gcap.sending': 'جارٍ الإرسال…',
  'droom.gcap.sendingLong': 'جارٍ الإرسال إلى موظف التحقق…',
  'droom.gcap.looksSharp': 'تبدو واضحة — أرسلها إلى موظف التحقق.',
  'droom.gcap.checkingQuality': 'جارٍ فحص جودة الصورة…',
  'droom.gcap.camOff': 'الكاميرا مغلقة — شغّلها مرة أخرى لالتقاط هذه الصورة.',
  'droom.gcap.notReady': 'الكاميرا ليست جاهزة بعد — امنحها ثانية وحاول مرة أخرى.',
  'droom.gcap.uploadFail': 'لم يُرفع ذلك — يُرجى المحاولة مرة أخرى.',
  'droom.gcap.retryLine': 'لنحاول مرة أخرى.',
  'droom.gcap.decline': 'لست مرتاحًا؟ أخبر موظف التحقق فقط — يمكنه سحب هذا الطلب، ويُسجَّل ذلك أيضًا.',
  'droom.gcap.remote.mayCapture': 'يمكن لموظف التحقق التقاط الصورة نيابةً عنك بينما تثبّت هذا — سترى ذلك يحدث هنا.',
  'droom.gcap.remote.firing': 'يلتقط موظف التحقق الصورة — اثبت مكانك',
  'droom.gcap.preview.dead': 'معاينة الكاميرا لا تظهر — لا يُلتقط أي شيء',
  'droom.gcap.preview.retry': 'جرّب الكاميرا مرة أخرى',
  'droom.gcap.backToCall': 'العودة إلى المكالمة',
  'droom.gcap.upload.title': 'أرسل المستند ملفًا بدلًا من ذلك',
  'droom.gcap.upload.cta': 'ارفع المستند بدلًا من ذلك',
  'droom.gcap.upload.sending': 'جارٍ إرسال ملفك…',
  'droom.gcap.upload.grade': 'الصورة الملتقطة هنا أثناء المكالمة دليل أقوى. لا تستخدم هذا إلا إذا لم تعمل الكاميرا — وسيراجعه موظف التحقق معك على أي حال.',
  'droom.gcap.upload.failed': 'تعذّر إرسال ذلك الملف — أخبر موظف التحقق أثناء المكالمة.',
  'droom.gcap.upload.notAllowed': 'لا يمكن إرسال هذا المستند ملفًا — سيرشدك موظف التحقق خلاله أثناء المكالمة.',
  'droom.gcap.upload.already': 'هذا المستند موجود بالفعل في سجلك — يمكن لموظف التحقق رؤيته.',
  'droom.gcap.upload.tooBig': 'هذا الملف كبير جدًا. جرّب صورة أصغر، أو اطلب المساعدة من موظف التحقق.',
  'droom.gcap.upload.wrongType': 'لا يمكن قراءة نوع هذا الملف. أرسل صورة JPG أو PNG للمستند.',
  'droom.georecheck.title': 'شارك موقعك الحالي',
  'droom.georecheck.asked': 'طلب موظف التحقق إعادة فحص موقعك وشبكتك أثناء هذه المكالمة.',
  'droom.georecheck.privacy': 'يُسجَّل موقعك وعنوان شبكتك مرة واحدة دليلَ تحقق — ولا يُتتبَّعان بشكل مستمر أبدًا.',
  'droom.georecheck.share': 'شارك موقعي',
  'droom.georecheck.requesting': 'جارٍ تحديد الموقع…',
  'droom.georecheck.posting': 'جارٍ التسجيل بأمان…',
  'droom.georecheck.done.title': 'تمت مشاركة موقعك مع موظف التحقق',
  'droom.georecheck.done.sub': 'جارٍ العودة إلى مكالمتك…',
  'droom.georecheck.done.missing.title': 'حُدّثت بيانات الشبكة',
  'droom.georecheck.done.missing.sub': 'لم يكن الموقع متاحًا، ولا بأس بذلك — يمكن لموظف التحقق رؤية بيانات شبكتك الحالية وسيتابع معك أثناء المكالمة.',
  'droom.georecheck.failed': 'تعذّر تسجيل ذلك الآن — يُرجى المحاولة مرة أخرى.',
  'droom.docside.front': 'الوجه الأمامي لـ {doc}',
  'droom.docside.back.now': 'والآن الوجه الخلفي لـ {doc}',
  'droom.docside.back.optionalNote': 'يحمل الوجه الخلفي العنوان — أضِفه إن استطعت. ويمكن لموظف التحقق أيضًا التقاطه معك أثناء المكالمة.',
  'droom.gcap.doc.titleBack': 'والآن اعرض الوجه الخلفي لـ {doc}',
  'droom.gcap.side.backTitle': 'والآن الوجه الخلفي للمستند',
  'droom.gcap.side.back': 'اقلب المستند وأمسك وجهه الخلفي ثابتًا داخل المربع.',
  'droom.gcap.side.front': 'أمسك الوجه الأمامي للمستند ثابتًا داخل المربع.',
  'droom.gcap.doc.titleTyped': 'اعرض {doc} أمام الكاميرا',
  'droom.gcap.flip': 'تبديل الكاميرا',
  'droom.gcap.rotate': 'يُصوَّر هذا المستند على أفضل وجه بالعرض — أدِر هاتفك إلى الوضع الأفقي.',

  // ── confirm details ────────────────────────────────────────────────────────
  'droom.confirm.subtitle': 'بياناتك',
  'droom.confirm.aria': 'تأكيد بياناتك',
  'droom.confirm.title': 'أكّد بياناتك',
  'droom.confirm.notProvided': 'غير مُقدَّم',
  'droom.confirm.sub': 'قُدّمت هذه البيانات عند إعداد التحقق الخاص بك — وتُطابَق هويتك معها. يُرجى مراجعة كل واحدة منها.',
  'droom.confirm.loading': 'جارٍ تحميل بياناتك…',
  'droom.confirm.readonlyNote': 'إذا كان شيء هنا غير صحيح، فأخبر موظف التحقق أثناء المكالمة — لا يمكن تعديل هذه البيانات هنا.',
  'droom.confirm.cta': 'هذه البيانات صحيحة',
  'droom.confirm.submitting': 'جارٍ تسجيل تأكيدك…',
  'droom.confirm.err': 'تعذّر تسجيل تأكيدك الآن. يُرجى المحاولة مرة أخرى.',

  // ── complete ───────────────────────────────────────────────────────────────
  'droom.complete.subtitle': 'تم الإرسال',
  'droom.complete.title': 'اكتمل كل شيء — شكرًا لك',
  'droom.complete.sub': 'شكرًا، {name}. أنهيت دورك في التحقق المرئي من الهوية. يراجعه فريقنا بعد ذلك.',
  'droom.complete.nameFallback': 'عزيزي العميل',
  'droom.complete.download': 'تنزيل إيصال الإرسال (PDF)',
  'droom.complete.downloadNote': 'ما أرسلته ومتى — وليس قرارًا. تصدر النتيجة عن {tenantName}.',
  'droom.complete.k.reference': 'المرجع',
  'droom.complete.k.completed': 'وقت الإكمال',
  'droom.complete.k.status': 'الحالة',
  'droom.complete.v.submitted': 'أُرسل للمراجعة',
  'droom.complete.next.review': 'يراجع فريقنا الطلب و**يفعّل حسابك** — عادةً خلال بضع ساعات.',
  'droom.complete.next.confirm': 'سيصلك **تأكيد عبر البريد الإلكتروني والرسائل النصية.**',
  'droom.complete.redirect.cta': 'المتابعة إلى {label}',
  'droom.complete.redirect.countdown': 'ستعود إلى {label} خلال {s} ث…',
  'droom.complete.redirect.now': 'جارٍ إعادتك إلى {label}…',
  'droom.complete.return': 'إغلاق هذه الصفحة',
  'droom.complete.recorded': 'سُجّلت هذه الجلسة بأمان لأغراض الامتثال · **بحماية KycHub**',
  'droom.consenthook.loadfail': 'تعذّر تحميل إشعار الموافقة. يُرجى المحاولة مرة أخرى.',
  'droom.consenthook.updated': 'حُدّث إشعار الموافقة. يُرجى المراجعة والتأكيد مرة أخرى.',
  'droom.consenthook.recordfail': 'تعذّر تسجيل موافقتك الآن. يُرجى المحاولة مرة أخرى.',
  'droom.quality.small.doc': 'خرجت الصورة صغيرة جدًا لقراءة المستند. اقترب حتى تملأ البطاقة الإطار، وحاول مرة أخرى.',
  'droom.quality.small.selfie': 'خرجت الصورة صغيرة جدًا. قرّب الهاتف قليلًا وحاول مرة أخرى.',
  'droom.quality.dark': 'مظلمة جدًا — انتقل إلى مكان أكثر إضاءة وحاول مرة أخرى.',
  'droom.quality.bright': 'ساطعة جدًا — ابتعد عن الضوء المباشر أو الوهج وحاول مرة أخرى.',
  'droom.quality.blurry': 'غير واضحة جدًا — ثبّت يدك وحاول مرة أخرى.',
  'droom.quality.unreadable': 'تعذّرت معالجة الصورة — يُرجى المحاولة مرة أخرى.',
};
